import { Gpt } from '@pcln/ad-unit'
import { Divider, Heading, P } from '@pcln/horizon'
import { SearchFormCard } from '@pcln/horizon-search-form'
import { Box, Card, Container, Hide, theme } from 'pcln-design-system'
import styled from 'styled-components'

import ComponentMetricsBoundary from '@/components/ComponentMetricsBoundary/ComponentMetricsBoundary'
import useFlightDetailsHeadline from '@/components/Flights/useFlightDetailsHeadline'
import HeroImageParallax from '@/components/Heros/HeroImageParallax'
import { HeroRedesignLayout } from '@/components/Heros/HeroRedesignLayout'
import StyledHeading from '@/components/StyledHeading'
import useBootstrapData from '@/hooks/useBootstrapData'
import { isModifyFlightDetails, isRebookFlightDetails } from '@/shared-utils/flight-rebook-helpers'

import ConditionalRender, { Experiment, Original } from '../../ConditionalRender'
import { FLIGHT_TYPE_INFO, MULTI_DESTINATION, ONE_WAY, ROUND_TRIP } from '../constants'
import FlightsContent from '../Content'
import HorizonFlightForm from '../HorizonForm/HorizonFlightForm'
import FlightsSearchForm from '../SearchForm/Form'
import { onSubmit } from '../SearchForm/utils'
import SubHeadline from '../SubHeadline'
import PhoneSalesBumper from './PhoneSalesBumper'

const BlockFlex = styled(Box)`
  position: relative;
  z-index: 1;
  ${theme.mediaQueries[3]} {
    display: flex;
  }
`

const HideAdUnit = styled(Hide)`
  flex: none;
  text-align: center;
  position: relative;
  margin: 16px;
  min-height: 100px;
  ${theme.mediaQueries[3]} {
    width: 300px;
    margin: 0 16px;
  }
`

const FLIGHTS_COMPANION_AD = <Gpt render={() => <div id='div-gpt-ad-1510165683878-0' />} />
const FLIGHTS_COMPANION_AD_VARIANT = <Gpt render={() => <div id='div-gpt-ad-1790171206911-0' />} />

type FlightsHeroSearchFormParams = Readonly<{
  headline: string
  isModify: boolean | undefined
  isRebook: boolean | undefined
}>

function FlightsHeroSearchForm({ headline, isModify, isRebook }: FlightsHeroSearchFormParams) {
  return (
    <ComponentMetricsBoundary componentId='SearchFormFlights'>
      <ConditionalRender experimentCode='USB_SEARCH_FLIGHTS_LANDING_HORIZON'>
        <Original>
          <Card
            data-testid='hero-search-form'
            borderWidth={0}
            color='background.lightest'
            borderRadius='2xl'
            boxShadowSize='xl'
          >
            <Box p={3}>
              <StyledHeading
                pb={1}
                tag='h1'
                color='text.heading'
                text={headline}
                textStyle={['heading3', null, null, null, 'heading2']}
              />
              {isModify && <SubHeadline />}

              <FlightsSearchForm
                isRebook={isRebook}
                isModify={isModify}
                onSubmit={onSubmit}
                flightTypes={
                  isRebook || isModify
                    ? [FLIGHT_TYPE_INFO[ROUND_TRIP], FLIGHT_TYPE_INFO[ONE_WAY]]
                    : [
                        FLIGHT_TYPE_INFO[ROUND_TRIP],
                        FLIGHT_TYPE_INFO[ONE_WAY],
                        FLIGHT_TYPE_INFO[MULTI_DESTINATION],
                      ]
                }
              />
            </Box>
            {!isModify && !isRebook && (
              <Hide xs sm>
                <Divider lineSlotBeforeClassName='my-0' />
                <Box color='background.light' borderRadius='2xl' boxShadowSize='xl' rounded='bottom'>
                  <PhoneSalesBumper />
                </Box>
              </Hide>
            )}
          </Card>
        </Original>
        <Experiment>
          <SearchFormCard data-testid='hero-search-form' variant='panel' width='full'>
            <Heading
              as='h1'
              palette='primary'
              shade='12'
              textStyle='heading3'
              className='m-0 pb-1 text-heading3 lg:text-heading2'
            >
              {headline}
            </Heading>
            {isModify && <SubHeadline />}
            <HorizonFlightForm
              isRebook={isRebook}
              isModify={isModify}
              onSubmit={onSubmit}
              pageName='landing'
            />
            {!isModify && !isRebook && (
              <div
                className='-mx-4 mt-4 -mb-4 overflow-hidden rounded-b-lg bg-neutral-1'
                data-testid='horizon-flight-bumper'
              >
                <Divider lineSlotBeforeClassName='my-0' />
                <div className='flex justify-center bg-neutral-1 p-4'>
                  <P textStyle='body2' palette='neutral' shade='12' align='center' className='m-0'>
                    Looking for international flight deals? Call us at 1 (833) 203-5879
                  </P>
                </div>
              </div>
            )}
          </SearchFormCard>
        </Experiment>
      </ConditionalRender>
    </ComponentMetricsBoundary>
  )
}

type FlightsHeroContentParams = Readonly<{
  headline: string
  isModify: boolean | undefined
  isRebook: boolean | undefined
}>

function FlightsHeroContent({ headline, isModify, isRebook }: FlightsHeroContentParams) {
  return (
    <Container maxWidth={1280}>
      <BlockFlex mx={3} py={[3, null, null, null, 5]}>
        <Box width={1}>
          <FlightsHeroSearchForm headline={headline} isModify={isModify} isRebook={isRebook} />
        </Box>
        <HideAdUnit xs sm>
          {FLIGHTS_COMPANION_AD}
        </HideAdUnit>
      </BlockFlex>
    </Container>
  )
}

function FlightsHeroRedesign({ headline, isModify, isRebook }: FlightsHeroContentParams) {
  return (
    <HeroRedesignLayout
      adBreakpoint='lg'
      adMinHeightClass='min-h-[100px]'
      adUnit={FLIGHTS_COMPANION_AD_VARIANT}
      outerClassName='relative mb-8'
    >
      <FlightsHeroSearchForm headline={headline} isModify={isModify} isRebook={isRebook} />
    </HeroRedesignLayout>
  )
}

function Desktop() {
  const headline = useFlightDetailsHeadline()
  const { flightRebookOrModifyDetails } = useBootstrapData()
  const isRebook = flightRebookOrModifyDetails && isRebookFlightDetails(flightRebookOrModifyDetails)
  const isModify = flightRebookOrModifyDetails && isModifyFlightDetails(flightRebookOrModifyDetails)
  const heroContentProps = {
    headline,
    isModify,
    isRebook,
  }

  return (
    <Box>
      <ConditionalRender experimentCode='CG_PRD_HERO_REDESIGN'>
        <Original>
          <HeroImageParallax
            isScrollable
            gptId='div-gpt-ad-1510165335112-0'
            slot='/3102/priceline.dart/air_homepage_hero_image'
            mb={4}
          >
            <FlightsHeroContent {...heroContentProps} />
          </HeroImageParallax>
        </Original>
        <Experiment>
          <FlightsHeroRedesign {...heroContentProps} />
        </Experiment>
      </ConditionalRender>
      <Hide xs sm color='background.lightest'>
        <FlightsContent />
      </Hide>
    </Box>
  )
}

export default Desktop
